<template>
  <div ref="wrapperRef">
    <ImageUploadNote :types />
    <FormHeading :h="3">{{ $tt('image_upload') }}</FormHeading>
    <ImageUploadInstructions :types />
    <v-divider />
    <ImageUploadOptions v-model="method" class="mb-7" />
    <v-expand-transition>
      <div v-if="method == 'form'">
        <ApplicantImageInputs
          v-for="applicant in applicants"
          :key="applicant.id"
          :images="applicant.files"
          :name="buildFullname(applicant.passport)"
          :types :loading
          @change="(type, file) => setFileToUpload(applicant.id, type, file)"
        />
      </div>
      <ImageUploadExternalMethod v-else :method />
    </v-expand-transition>

    <v-expand-transition>
      <ImageUploadLaterSuggestion
        v-if="failed && method == 'form'"
        @agree="method = 'later'"
      />
    </v-expand-transition>
  </div>
</template>

<script setup lang="ts">
import { snackbarInjectionKey } from '~/assets/injection-keys';
import { buildFullname } from '~/helpers/format';
import { uploadPhoto } from '~/helpers/http';
import { convertToBase64, compressFile } from '~/helpers/media';
import type { FileTransferMethod, VisaForm, ApplicantPhotoType, UploadPhotoReqBody, ApplicantWithFiles } from '~/types';
import { ApplicantImageInputs } from '#components'; // defineEmits not typed properly on auto imported components after upgrading to 3.19.0 -- should be fixed in next version

const snackbar = inject(snackbarInjectionKey)!;
const applicants = defineModel<ApplicantWithFiles[]>({ required: true });
const method = defineModel<FileTransferMethod>('method', { required: true });
const props = defineProps<{
  formId: VisaForm['id'];
  types: ApplicantPhotoType[];
}>();

defineExpose({ upload });

const loading = ref(false);
const uploadReqBodies: UploadPhotoReqBody[] = [];

const validationFailed = ref(false);
const uploadFailed = ref(false);
const failed = computed(() => validationFailed.value || uploadFailed.value);
suggestLaterOnSubmitFail();

function suggestLaterOnSubmitFail() {
  const { on } = useEmitter().formValidationFailedBus;
  const unsubscribe = on(() => {
    validationFailed.value = true;
    unsubscribe();
  });
}

async function setFileToUpload(id: string, type: ApplicantPhotoType, file: File | null) {
  if (!file) return;
  const compressedFile = await compressFile(file).catch(() => file);
  const base64 = await convertToBase64(compressedFile);
  const applicant = findApplicant(id);
  const reqBody: UploadPhotoReqBody = {
    formId: props.formId,
    applicantId: id,
    base64, type
  };
  const existsIndex = uploadReqBodies.findIndex(b => b.applicantId == id && b.type == type);
  if (existsIndex == -1) uploadReqBodies.push(reqBody);
  else uploadReqBodies[existsIndex] = reqBody;
  applicant.files[type] = '';
}

async function upload() {
  if (!uploadReqBodies.length) return;
  loading.value = true;
  const promises = uploadReqBodies.map(uploadPhoto);
  const results = await Promise.all(promises).catch(err => {
    loading.value = false;
    uploadFailed.value = true;
    snackbar.value?.showError('photo_upload');
    throw new Error(err);
  });
  // Assign
  for (const result of results) {
    if (!result) throw new Error('No result from uploadPhoto');
    const { url, applicantId, type } = result;
    const applicant = findApplicant(applicantId);
    applicant.files[type] = url;
  }
  loading.value = false;
}

function findApplicant(id: string) {
  const applicant = applicants.value.find(a => a.id == id);
  if (!applicant) throw new Error(`Applicant with ID "${id}" not found`);
  return applicant;
}
</script>